import React, { useState } from 'react';
import { Send, Download, FileText } from 'lucide-react';
import { generateDocument } from '../services/openai';
import { supabase } from '../lib/supabase';
import { FileUpload } from './FileUpload';

export type DocumentType = 'management' | 'project' | 'unit' | 'session' | 'evaluation';

interface DocumentGeneratorProps {
  type: DocumentType;
  onDocumentCreated?: () => void;
}

export function DocumentGenerator({ type, onDocumentCreated }: DocumentGeneratorProps) {
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [referenceDocument, setReferenceDocument] = useState<{ content: string; fileName: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Por favor, proporciona un título para tu documento');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const generatedContent = await generateDocument(
        type, 
        prompt,
        referenceDocument || undefined
      );
      setResult(generatedContent || '');

      // Try to save to database if available
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: saveError } = await supabase
            .from('documents')
            .insert({
              title,
              content: generatedContent,
              type,
              user_id: user.id,
            });

          if (saveError) {
            console.warn('Could not save to database:', saveError);
          } else {
            onDocumentCreated?.();
          }
        }
      } catch (dbError) {
        console.warn('Database operation failed:', dbError);
        // Continue without database save
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'txt' | 'pdf') => {
    const element = document.createElement('a');
    const file = new Blob([result], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${title}.${format === 'pdf' ? 'txt' : format}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getTypeLabel = (type: DocumentType) => {
    const labels = {
      management: 'Documento de Gestión',
      project: 'Proyecto',
      unit: 'Unidad',
      session: 'Sesión',
      evaluation: 'Evaluación'
    };
    return labels[type];
  };

  return (
    <div className="bg-white rounded-lg shadow-xl p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Generar {getTypeLabel(type)}
        </h2>
        <p className="text-gray-600">
          Completa los campos para generar tu documento con IA
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            Subir Documento de Referencia (Opcional)
          </h3>
          <FileUpload onUploadComplete={(content, fileName) => 
            setReferenceDocument({ content, fileName })
          } />
          {referenceDocument && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-green-700">
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">
                    Usando referencia: {referenceDocument.fileName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setReferenceDocument(null)}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                >
                  Remover
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Título del Documento *
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder={`Ingresa un título para tu ${getTypeLabel(type).toLowerCase()}`}
            required
          />
        </div>

        <div>
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
            Describe tus requerimientos
          </label>
          <textarea
            id="prompt"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder={`Describe los requerimientos para tu ${getTypeLabel(type).toLowerCase()}...`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <button
            type="submit"
            disabled={loading || !title.trim()}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Generar {getTypeLabel(type)}
              </>
            )}
          </button>

          {result && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleExport('txt')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar TXT
              </button>
            </div>
          )}
        </div>
      </form>

      {result && (
        <div className="mt-8 border-t pt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Contenido Generado</h3>
            <span className="text-sm text-gray-500">
              {result.length} caracteres
            </span>
          </div>
          <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
              {result}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}